import React, { useState } from "react";
import {
  useGetEventRSVPsQuery,
  useGetPendingRSVPsQuery,
  useAcceptRSVPMutation,
  useDeclineRSVPMutation,
} from "../../../services/api";
import "./ViewRSVPsModal.css";

// Icons
const IconClose = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconUser = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconCheck = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconX = (props) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function RSVPModal({ eventId, onClose, initialTab = "accepted" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [removingId, setRemovingId] = useState(null);

  // Queries
  const { data: acceptedData, isLoading: isLoadingAccepted } = useGetEventRSVPsQuery(eventId);
  const { data: pendingData, isLoading: isLoadingPending } = useGetPendingRSVPsQuery(eventId);

  // Mutations
  const [acceptRSVP, { isLoading: isAccepting }] = useAcceptRSVPMutation();
  const [declineRSVP, { isLoading: isDeclining }] = useDeclineRSVPMutation();

  const acceptedRsvps = acceptedData?.rsvps || [];
  const acceptedCount = acceptedData?.count || 0;
  const pendingRsvps = pendingData?.rsvps || [];
  const pendingCount = pendingData?.count || 0;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAccept = async (rsvpId) => {
    try {
      await acceptRSVP({ eventId, rsvpId }).unwrap();
    } catch (err) {
      console.error("Failed to accept RSVP:", err);
    }
  };

  const handleDecline = async (rsvpId) => {
    try {
      await declineRSVP({ eventId, rsvpId }).unwrap();
    } catch (err) {
      console.error("Failed to decline RSVP:", err);
    }
  };

  const handleRemove = async (rsvpId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from this event? The event will no longer be visible to them.`)) {
      return;
    }
    setRemovingId(rsvpId);
    try {
      await declineRSVP({ eventId, rsvpId }).unwrap();
    } catch (err) {
      console.error("Failed to remove RSVP:", err);
    } finally {
      setRemovingId(null);
    }
  };

  const isProcessing = isAccepting || isDeclining;
  const isLoading = activeTab === "accepted" ? isLoadingAccepted : isLoadingPending;
  const rsvps = activeTab === "accepted" ? acceptedRsvps : pendingRsvps;
  const count = activeTab === "accepted" ? acceptedCount : pendingCount;

  return (
    <div className="rsvp-modal-overlay" onClick={handleOverlayClick}>
      <div className="rsvp-modal">
        {/* Header */}
        <div className="rsvp-modal-header">
          <h2>Event RSVPs</h2>
          <button
            type="button"
            className="rsvp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        {/* Tabs */}
        <div className="rsvp-tabs">
          <button
            className={`rsvp-tab ${activeTab === "accepted" ? "active" : ""}`}
            onClick={() => setActiveTab("accepted")}
          >
            Confirmed ({acceptedCount})
          </button>
          <button
            className={`rsvp-tab ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            Pending ({pendingCount})
          </button>
        </div>

        {/* Content */}
        <div className="rsvp-modal-content">
          {isLoading ? (
            <div className="rsvp-loading">
              Loading {activeTab === "accepted" ? "RSVPs" : "pending requests"}...
            </div>
          ) : rsvps.length === 0 ? (
            <div className="rsvp-empty">
              <IconUser style={{ width: 48, height: 48, opacity: 0.3 }} />
              <p>{activeTab === "accepted" ? "No RSVPs yet" : "No pending requests"}</p>
            </div>
          ) : (
            <>
              <div className="rsvp-count">
                <span className="count-number">{count}</span>
                <span className="count-label">
                  {activeTab === "accepted"
                    ? `confirmed attendee${count !== 1 ? "s" : ""}`
                    : `pending request${count !== 1 ? "s" : ""}`}
                </span>
              </div>

              <div className="rsvp-list">
                {rsvps.map((rsvp) => {
                  const userName = `${rsvp.user?.firstName || ""} ${rsvp.user?.lastName || ""}`.trim();
                  const isCurrentlyRemoving = removingId === rsvp._id;

                  return (
                    <div key={rsvp._id} className={`rsvp-item ${activeTab === "pending" ? "pending-item" : ""}`}>
                      <div className="rsvp-avatar">
                        {rsvp.user?.firstName?.charAt(0) || ""}
                        {rsvp.user?.lastName?.charAt(0) || ""}
                      </div>
                      <div className="rsvp-info">
                        <div className="rsvp-name">{userName}</div>
                        <div className="rsvp-email">
                          {activeTab === "accepted"
                            ? rsvp.user?.email
                            : rsvp.user?.jobTitle || "No job title"}
                        </div>
                      </div>
                      <div className="rsvp-actions">
                        {activeTab === "accepted" ? (
                          <button
                            className="action-btn remove-btn"
                            onClick={() => handleRemove(rsvp._id, userName)}
                            disabled={isProcessing}
                            title="Remove from event"
                          >
                            <IconX />
                            <span>{isCurrentlyRemoving ? "Removing..." : "Remove"}</span>
                          </button>
                        ) : (
                          <>
                            <button
                              className="action-btn accept-btn"
                              onClick={() => handleAccept(rsvp._id)}
                              disabled={isProcessing}
                              title="Accept"
                            >
                              <IconCheck />
                              <span>Accept</span>
                            </button>
                            <button
                              className="action-btn decline-btn"
                              onClick={() => handleDecline(rsvp._id)}
                              disabled={isProcessing}
                              title="Decline"
                            >
                              <IconX />
                              <span>Decline</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="rsvp-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
